import React from 'react';
import {
  ActivityIndicator,
  Button,
  Dialog,
  Portal,
  useTheme,
} from 'react-native-paper';
import { t, Trans } from '@lingui/macro';
import styled from 'styled-components/native';

import { H2 } from '@src/components/Typography';
import usePrimeDiscountCode from '@src/hooks/usePrimeDiscountCode';
import useSnackBar from '@src/hooks/useSnackBar';
import { i18n } from '@src/i18n';
import Clipboard from '@src/modules/Clipboard';

import { ActionButton, DialogBody, DiscountCode } from './styled';

type Props = {
  hasPrime: boolean;
  onConfirm?(): void;
  onDiscard?(): void;
  onGetCodeSuccessful?(code: string): void;
};
export const Title = styled(H2)`
  font-weight: 700;
`;
const Component: React.FC<Props> = (props) => {
  const { hasPrime, onConfirm, onDiscard, onGetCodeSuccessful } = props;
  const [buyPrimeVisible, setBuyPrimeVisible] = React.useState(false);
  const [getCodeVisible, setGetCodeVisible] = React.useState(false);
  const theme = useTheme();
  const {
    discount,
    makeRequest: getDiscountCode,
    loading,
  } = usePrimeDiscountCode();
  const snackBar = useSnackBar();

  const hideBuyPrimeDialog = () => setBuyPrimeVisible(false);
  const showBuyPrimeDialog = () => setBuyPrimeVisible(true);
  const hideGetCodeDialog = () => setGetCodeVisible(false);
  const showGetCodeDialog = () => {
    getDiscountCode();
    setGetCodeVisible(true);
  };

  const handleGetDiscountPress = () => {
    if (!hasPrime) {
      showBuyPrimeDialog();
    } else {
      showGetCodeDialog();
    }
  };

  const handleConfirmPress = () => {
    hideBuyPrimeDialog();
    onConfirm && onConfirm();
  };

  const handleDiscardPress = () => {
    hideBuyPrimeDialog();
    onDiscard && onDiscard();
  };

  const discountCode = discount?.code;

  const handlePrimeCopyCodePress = () => {
    if (discount?.code) {
      Clipboard.setString(discount.code);
      snackBar.show({
        content: i18n._(t`Discount code copied to your clipboard`),
      });
    } else {
      snackBar.show({ content: i18n._(t`Something Wrong`) });
    }
  };

  const handlePrimeBuyTourPress = () => {
    hideGetCodeDialog();
    onGetCodeSuccessful && onGetCodeSuccessful(discount?.code || '');
  };

  return (
    <>
      <Portal>
        <Dialog visible={getCodeVisible} onDismiss={hideGetCodeDialog}>
          {loading ? (
            <ActivityIndicator size="large" />
          ) : (
            <>
              <Dialog.Title>
                <Trans>Discount Code</Trans>
              </Dialog.Title>
              <Dialog.Content>
                <DialogBody>
                  <Trans>Your code is: {discountCode}</Trans>
                </DialogBody>
              </Dialog.Content>
              <Dialog.Actions>
                <ActionButton
                  preset="secondary"
                  onPress={handlePrimeCopyCodePress}
                >
                  <Trans>Copy discount code</Trans>
                </ActionButton>
                <ActionButton onPress={handlePrimeBuyTourPress}>
                  <Trans>Buy Tour</Trans>
                </ActionButton>
              </Dialog.Actions>
            </>
          )}
        </Dialog>
        <Dialog visible={buyPrimeVisible} onDismiss={hideBuyPrimeDialog}>
          <Dialog.Title>
            <Trans>Notice</Trans>
          </Dialog.Title>
          <Dialog.Content>
            <H2>
              <Trans>This feature is only for prime users</Trans>
            </H2>
            <DialogBody>
              <Trans>Do you want to buy a prime plan?</Trans>
            </DialogBody>
          </Dialog.Content>
          <Dialog.Actions>
            <ActionButton preset="secondary" onPress={handleDiscardPress}>
              <Trans>Cancel</Trans>
            </ActionButton>
            <ActionButton preset="primary" onPress={handleConfirmPress}>
              <Trans>Ok</Trans>
            </ActionButton>
          </Dialog.Actions>
        </Dialog>
      </Portal>
      <Button
        mode="outlined"
        labelStyle={{
          fontFamily: theme.fonts.medium.fontFamily,
          fontWeight: theme.fonts.medium.fontWeight,
        }}
        style={{
          borderWidth: 1,
          height: 40,
          paddingHorizontal: 10,
          borderRadius: 8,
          alignItems: 'center',
          borderColor: theme.colors.primary,
          marginHorizontal: '15%',
        }}
        onPress={handleGetDiscountPress}
      >
        <Trans>Get Discount Code</Trans>
      </Button>
    </>
  );
};

Component.displayName = 'DiscountCodeButton';

export default React.memo(Component);
